/**
 * Router Utilities from app-shell
 *
 * This module exports router guard utilities that customer applications
 * can use in their own routers (apps/<customer>/src/router/).
 *
 * The actual router instance is created in each customer app to avoid
 * circular dependencies between app-shell and features.
 */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { authGuard, useAuth0 } from '@auth0/auth0-vue';
import { reportAuthFlowBlocked } from '@peanuds/shared/observability/runtime-observability';
import {
  resolvePostAuthNavigation,
  type PostAuthNavigationRouteKind,
} from '../auth0/post-auth-navigation';
import { resolvePostLoginRedirectTarget } from '../auth0/post-login-redirect-target';
import { useAuthSessionStore } from '../auth0/store/auth-session.store';
import { appPinia } from '../plugins/pinia';
import { isUserContextLoadError } from '../store/user-context.errors';

const INITIAL_AUTH_SESSION_TIMEOUT_MS = 3_000;

const getRedirectTarget = (route: RouteLocationNormalized) => {
  const redirect = route.query?.redirect;
  const candidate = Array.isArray(redirect) ? redirect[0] : redirect;

  return resolvePostLoginRedirectTarget(candidate);
};

const getCurrentRouteRedirectTarget = (route: RouteLocationNormalized) => {
  return resolvePostLoginRedirectTarget(route.fullPath);
};

const getUnauthenticatedRoute = (route: RouteLocationNormalized) => {
  return { name: 'login', query: { redirect: getCurrentRouteRedirectTarget(route) } };
};

const getPostAuthRouteKind = (route: RouteLocationNormalized): PostAuthNavigationRouteKind => {
  if (route.name === 'collect-basic-info') {
    return 'basic-info';
  }

  if (route.name === 'create-organization') {
    return 'create-organization';
  }

  if (route.name === 'select-organization') {
    return 'select-organization';
  }

  return 'application';
};

const getRouteNameTag = (route: RouteLocationNormalized): string | null => {
  return typeof route.name === 'string' ? route.name : null;
};

// Re-export Auth0's authGuard for per-route usage
export { authGuard };

/**
 * Global auth guard - requires auth by default
 * Usage in customer app router:
 *
 * router.beforeEach(createAuthGuard());
 *
 * All routes require auth UNLESS meta.requiresAuth is set to false
 *
 * Unauthenticated users are redirected to /login.
 */
export function createAuthGuard() {
  return async (to: RouteLocationNormalized) => {
    // Check if any matched route explicitly opts out of auth
    const authDirective = [...to.matched]
      .reverse()
      .find((record) => record.meta.requiresAuth === true || record.meta.requiresAuth === false)
      ?.meta.requiresAuth;
    const isPublic = authDirective === false;
    if (isPublic) {
      return true;
    }

    const auth0 = useAuth0();
    const authSessionStore = useAuthSessionStore(appPinia);
    const currentPostAuthRoute = getPostAuthRouteKind(to);
    const resolveAuthenticatedNavigation = () => {
      const decision = resolvePostAuthNavigation({
        currentRoute: currentPostAuthRoute,
        redirectTarget:
          currentPostAuthRoute === 'application'
            ? getCurrentRouteRedirectTarget(to)
            : getRedirectTarget(to),
        state: {
          requiresBasicInfoCollection: authSessionStore.requiresBasicInfoCollection === true,
          requiresOrganizationCreation: authSessionStore.requiresOrganizationCreation === true,
          requiresOrganizationSelection: authSessionStore.requiresOrganizationSelection === true,
        },
      });

      if (decision.kind === 'route') {
        return decision.route;
      }

      if (decision.kind === 'redirect-target') {
        return decision.target;
      }

      return true;
    };

    if (
      authSessionStore.isReady &&
      authSessionStore.jwtToken &&
      authSessionStore.idToken &&
      !authSessionStore.isTokenExpired
    ) {
      console.debug('[AuthGuard] Using existing ready session', {
        to: to.fullPath,
        requiresBasicInfoCollection: authSessionStore.requiresBasicInfoCollection,
        requiresOrganizationCreation: authSessionStore.requiresOrganizationCreation,
        requiresOrganizationSelection: authSessionStore.requiresOrganizationSelection,
        needsOrganizationScopedSession: authSessionStore.needsOrganizationScopedSession,
        isOrganizationScoped: authSessionStore.isOrganizationScoped,
      });

      return resolveAuthenticatedNavigation();
    }

    if (authSessionStore.jwtToken && authSessionStore.isTokenExpired) {
      reportAuthFlowBlocked({
        operation: 'auth-guard',
        failureKind: 'local-token-expired',
        tags: {
          routeName: getRouteNameTag(to),
          hasIdToken: Boolean(authSessionStore.idToken),
        },
      });

      console.debug('[AuthGuard] Local token is expired; attempting Auth0 session recovery', {
        to: to.fullPath,
      });
    }

    if (
      auth0.isLoading &&
      !auth0.isLoading.value &&
      auth0.isAuthenticated &&
      !auth0.isAuthenticated.value
    ) {
      console.debug(
        '[AuthGuard] Redirecting to login because Auth0 is settled and no app session exists',
        {
          to: to.fullPath,
        },
      );
      return getUnauthenticatedRoute(to);
    }

    let result;

    try {
      result = await authSessionStore.initializeSession({
        timeoutMs: INITIAL_AUTH_SESSION_TIMEOUT_MS,
        auth0Client: auth0,
      });
      console.debug('[AuthGuard] initializeSession completed', {
        to: to.fullPath,
        status: result.status,
        reason: result.reason,
      });
    } catch (error) {
      if (isUserContextLoadError(error)) {
        throw error;
      }

      reportAuthFlowBlocked({
        operation: 'initialize-session',
        failureKind: 'exception',
        tags: {
          routeName: getRouteNameTag(to),
        },
      });
      console.warn('[AuthGuard] initializeSession threw, redirecting to login', {
        to: to.fullPath,
      });
      return getUnauthenticatedRoute(to);
    }

    if (result.status === 'failed' && isUserContextLoadError(result.error)) {
      throw result.error;
    }

    if (result.status === 'redirecting') {
      return false;
    }

    if (result.status !== 'authenticated') {
      reportAuthFlowBlocked({
        operation: 'initialize-session',
        failureKind: typeof result.reason === 'string' ? result.reason : result.status,
        tags: {
          routeName: getRouteNameTag(to),
          status: result.status,
        },
      });
      console.warn('[AuthGuard] initializeSession did not authenticate, redirecting to login', {
        to: to.fullPath,
        status: result.status,
        reason: result.reason,
      });
      return getUnauthenticatedRoute(to);
    }

    return resolveAuthenticatedNavigation();
  };
}

/**
 * Payment flow guard - validates payment-processed route has required query parameters
 * Usage in customer app router:
 *
 * router.beforeEach(createPaymentFlowGuard());
 *
 * The new contract requires query parameters:
 * - kind: 'external-challenge' | 'internal-challenge' | 'external-direct' | 'internal-direct'
 * - challengeId: required for challenge-based flows
 * - transferId: required for direct flows
 */
export function createPaymentFlowGuard() {
  const VALID_KINDS = [
    'external-challenge',
    'internal-challenge',
    'external-direct',
    'internal-direct',
  ] as const;

  const isValidId = (value: unknown): value is string => {
    if (value === null) return false;
    if (Array.isArray(value))
      return value.length > 0 && typeof value[0] === 'string' && value[0].trim().length > 0;
    return typeof value === 'string' && value.trim().length > 0;
  };

  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    if (to.name === 'payment-processed') {
      const kind = to.query.kind;
      const challengeId = to.query.challengeId;
      const transferId = to.query.transferId;

      const kindValue = Array.isArray(kind) ? kind[0] : kind;
      const isValidKind =
        typeof kindValue === 'string' &&
        VALID_KINDS.includes(kindValue as (typeof VALID_KINDS)[number]);

      if (!isValidKind) {
        return next({ name: 'dashboard' });
      }

      if (kindValue === 'external-challenge' || kindValue === 'internal-challenge') {
        if (!isValidId(challengeId)) {
          return next({ name: 'dashboard' });
        }
      }

      if (kindValue === 'external-direct' || kindValue === 'internal-direct') {
        if (!isValidId(transferId)) {
          return next({ name: 'dashboard' });
        }
      }
    }

    next();
  };
}

/**
 * Meta title handler for setting document title based on route meta
 * Usage in customer app router:
 *
 * router.afterEach(createTitleHandler('MyApp'));
 */
export function createTitleHandler(baseTitle: string) {
  return (to: RouteLocationNormalized) => {
    if (typeof to.meta.title === 'function') {
      document.title = to.meta.title(to);
    } else if (typeof to.meta.title === 'string') {
      document.title = to.meta.title;
    } else {
      document.title = baseTitle;
    }
  };
}
